import { loop, Cmd } from 'redux-loop';
import { pathOr } from 'ramda';
import produce from 'immer';
import { EntityContainer } from '@prezi/common-components';
import { actions } from './actions/actions';
import { unifiedPrivacyActions } from './actions/unifiedPrivacyActions';
import { referralActions } from './actions/referral';
import { viewLinkActions } from './actions/viewLinkActions';
import { getDocumentDetails, getUnifiedPrivacyAndPermissions, setDocumentPublished, setDocumentReusable, setDocumentUnifiedPrivacy, updateDocumentDetails } from '../api/presentation';
import { getCollaborators, updateCollaborator, deleteCollaborator, updateTeamPermission, deleteTeamPermission } from '../api/share';
import { getTeamMembers } from '../api/contacts';
import { getReferralData } from '../api/referral';
import { FolderService } from '../api/folder';
import { getViewlinks, updateViewlink, createViewlink, getUnlistedShareLink, revokeViewlink, createOrRegenerateUnlistedShareLink, deleteUnlistedLink } from '../api/viewlink';
import { mapUserPermissions } from '../api/userPermissions';
import { getFeatureSwitches } from '../api/featureSwitches';
function setLoading(state) {
  return {
    ...state,
    loading: true,
    error: null
  };
}
function setValue(state, value) {
  return {
    ...state,
    initialized: true,
    loading: false,
    error: null,
    value
  };
}
function setError(state, error) {
  return {
    ...state,
    loading: false,
    error
  };
}
const getReducer = initialState => (state = initialState, action) => {
  switch (action.type) {
    case actions.setPage.actionType:
      {
        return produce(state, newState => {
          newState.page = action.payload;
        });
      }
    case actions.getDocument.actionType:
      {
        return loop(produce(state, newState => {
          newState.document = setLoading(state.document);
        }), Cmd.run(() => getDocumentDetails(state.documentOid), {
          successActionCreator: actions.getDocumentSuccess,
          failActionCreator: actions.getDocumentFailure
        }));
      }
    case actions.getDocumentSuccess.actionType:
      {
        var _state$featureSwitche;
        const isPresentDocument = action.payload.schema_version_name === 'PITCH';
        const isDesignDocument = action.payload.schema_version_name === 'INFOGRAM';
        const supportsViewLinks = isPresentDocument || isDesignDocument && ((_state$featureSwitche = state.featureSwitches.value) === null || _state$featureSwitche === void 0 ? void 0 : _state$featureSwitche['prezi-design-analytics-viewlinks']);
        const getViewLinksIfNeeded = supportsViewLinks ? Cmd.action(viewLinkActions.getViewlinks()) : Cmd.none;
        const newState = produce(state, newState => {
          newState.document = setValue(state.document, action.payload);
          if (!supportsViewLinks) {
            newState.viewlinks.initialized = true;
            newState.viewlinks.loading = false;
          }
        });
        return loop(newState, getViewLinksIfNeeded);
      }
    case actions.getDocumentFailure.actionType:
      {
        return produce(state, newState => {
          newState.document = setError(state.document, action.payload);
        });
      }
    case actions.updateDocument.actionType:
      {
        return loop({
          ...state,
          document: setLoading(state.document)
        }, Cmd.run(() => updateDocumentDetails(action.payload.oid, action.payload.item, action.payload.tags), {
          successActionCreator: response => actions.updateDocumentSuccess(response, action.payload.redirectPage),
          failActionCreator: actions.updateDocumentFailure
        }));
      }
    case actions.updateDocumentSuccess.actionType:
      {
        return {
          ...state,
          page: action.payload.redirectPage || state.page,
          document: setValue(state.document, action.payload.item)
        };
      }
    case actions.updateDocumentFailure.actionType:
      {
        return {
          ...state,
          document: setError(state.document, action.payload)
        };
      }
    case actions.getUnifiedPrivacyWithUserPermissions.actionType:
      {
        return loop({
          ...state,
          unifiedPrivacy: setLoading(state.unifiedPrivacy),
          userPermissions: setLoading(state.userPermissions)
        }, Cmd.run(() => getUnifiedPrivacyAndPermissions(state.documentOid), {
          successActionCreator: actions.getUnifiedPrivacyWithUserPermissionsSuccess,
          failActionCreator: actions.getUnifiedPrivacyWithUserPermissionsFailure
        }));
      }
    case actions.getUnifiedPrivacyWithUserPermissionsSuccess.actionType:
      {
        return {
          ...state,
          unifiedPrivacy: setValue(state.unifiedPrivacy, action.payload),
          userPermissions: setValue(state.userPermissions, mapUserPermissions(action.payload.permissions))
        };
      }
    case actions.getUnifiedPrivacyWithUserPermissionsFailure.actionType:
      {
        return {
          ...state,
          unifiedPrivacy: setError(state.unifiedPrivacy, action.payload),
          userPermissions: setError(state.userPermissions, action.payload)
        };
      }
    case actions.getFeatureSwitches.actionType:
      {
        return loop(produce(state, newState => {
          newState.featureSwitches = setLoading(state.featureSwitches);
        }), Cmd.run(() => getFeatureSwitches(), {
          successActionCreator: actions.getFeatureSwitchesSuccess,
          failActionCreator: actions.getFeatureSwitchesFailure
        }));
      }
    case actions.getFeatureSwitchesSuccess.actionType:
      {
        return produce(state, newState => {
          newState.featureSwitches = setValue(state.featureSwitches, action.payload);
        });
      }
    case actions.getFeatureSwitchesFailure.actionType:
      {
        return produce(state, newState => {
          newState.featureSwitches = setError(state.featureSwitches, action.payload);
        });
      }
    case actions.getCollaborators.actionType:
      {
        return loop(produce(state, newState => {
          newState.collaborators = setLoading(state.collaborators);
          newState.organization = setLoading(state.organization);
        }), Cmd.run(() => getCollaborators(state.documentOid), {
          successActionCreator: actions.getCollaboratorsSuccess,
          failActionCreator: actions.getCollaboratorsFailure
        }));
      }
    case actions.getCollaboratorsSuccess.actionType:
      {
        return produce(state, newState => {
          newState.collaborators = setValue(state.collaborators, EntityContainer.fromArray(action.payload.individual, pathOr('', ['email'])));
          newState.organization = setValue(state.organization, action.payload.organization);
        });
      }
    case actions.getCollaboratorsFailure.actionType:
      {
        return produce(state, newState => {
          newState.collaborators = setError(state.collaborators, action.payload);
          newState.organization = setError(state.organization, action.payload);
        });
      }
    case actions.updateCollaborator.actionType:
      {
        const {
          email,
          permission
        } = action.payload;
        return loop(produce(state, newState => {
          newState.collaborators = setLoading(state.collaborators);
        }), Cmd.run(() => {
          if (state.referral.error || !state.referral.value) {
            return updateCollaborator(state.documentOid, email, permission);
          }
          return updateCollaborator(state.documentOid, email, permission, state.referral.value.tokens.em);
        }, {
          successActionCreator: actions.updateCollaboratorSuccess,
          failActionCreator: actions.updateCollaboratorFailure
        }));
      }
    case actions.updateCollaboratorSuccess.actionType:
      {
        return produce(state, newState => {
          newState.collaborators = setValue(state.collaborators, state.collaborators.value.append(action.payload.email, action.payload));
        });
      }
    case actions.updateCollaboratorFailure.actionType:
      {
        return produce(state, newState => {
          newState.collaborators = setError(state.collaborators, action.payload);
        });
      }
    case actions.removeCollaborator.actionType:
      {
        return loop(produce(state, newState => {
          newState.collaborators = setLoading(state.collaborators);
        }), Cmd.run(() => deleteCollaborator(state.documentOid, action.payload), {
          successActionCreator: () => actions.removeCollaboratorSuccess(action.payload),
          failActionCreator: actions.removeCollaboratorFailure
        }));
      }
    case actions.removeCollaboratorSuccess.actionType:
      {
        return produce(state, newState => {
          newState.collaborators = setValue(state.collaborators, state.collaborators.value.exclude(action.payload.toString()));
        });
      }
    case actions.removeCollaboratorFailure.actionType:
      {
        return produce(state, newState => {
          newState.collaborators = setError(state.collaborators, action.payload);
        });
      }
    case actions.updateOrganization.actionType:
      {
        return loop({
          ...state,
          organization: setLoading(state.organization)
        }, Cmd.run(() => updateTeamPermission(state.documentOid, action.payload), {
          successActionCreator: actions.updateOrganizationSuccess,
          failActionCreator: actions.updateOrganizationFailure
        }));
      }
    case actions.updateOrganizationSuccess.actionType:
      {
        return {
          ...state,
          organization: setValue(state.organization, action.payload)
        };
      }
    case actions.updateOrganizationFailure.actionType:
      {
        return {
          ...state,
          organization: setError(state.organization, action.payload)
        };
      }
    case actions.removeOrganization.actionType:
      {
        return loop({
          ...state,
          organization: setLoading(state.organization)
        }, Cmd.run(() => deleteTeamPermission(state.documentOid), {
          successActionCreator: actions.removeOrganizationSuccess,
          failActionCreator: actions.removeOrganizationFailure
        }));
      }
    case actions.removeOrganizationSuccess.actionType:
      {
        return {
          ...state,
          organization: setValue(state.organization, {
            ...state.organization.value,
            can_edit: false,
            can_view: false,
            can_comment: false
          })
        };
      }
    case actions.removeOrganizationFailure.actionType:
      {
        return {
          ...state,
          organization: setError(state.organization, action.payload)
        };
      }
    case viewLinkActions.getViewlinks.actionType:
      {
        return loop({
          ...state,
          viewlinks: setLoading(state.viewlinks)
        }, Cmd.run(() => getViewlinks(state.documentOid), {
          successActionCreator: viewLinkActions.getViewlinksSuccess,
          failActionCreator: viewLinkActions.getViewlinksFailure
        }));
      }
    case viewLinkActions.getViewlinksSuccess.actionType:
      {
        return {
          ...state,
          viewlinks: setValue(state.viewlinks, EntityContainer.fromArray(action.payload, pathOr('', ['link_id'])).filter(viewLink => !viewLink.revoked))
        };
      }
    case viewLinkActions.getViewlinksFailure.actionType:
      {
        return {
          ...state,
          viewlinks: setError(state.viewlinks, action.payload)
        };
      }
    case viewLinkActions.updateViewlink.actionType:
      {
        const {
          viewLink
        } = action.payload;
        return loop(produce(state, newState => {
          newState.viewlinks = setLoading(state.viewlinks);
        }), Cmd.run(() => updateViewlink(viewLink.link_id, viewLink.name, viewLink.visible_on_analytics, viewLink.viewer_identification), {
          successActionCreator: viewLinkActions.updateViewlinkSuccess,
          failActionCreator: viewLinkActions.updateViewlinkFailure
        }));
      }
    case viewLinkActions.updateViewlinkSuccess.actionType:
      {
        return produce(state, newState => {
          newState.viewlinks = setValue(state.viewlinks, state.viewlinks.value.update(action.payload.link_id, action.payload));
        });
      }
    case viewLinkActions.updateViewlinkFailure.actionType:
      {
        return produce(state, newState => {
          newState.viewlinks = setError(state.viewlinks, action.payload);
        });
      }
    case viewLinkActions.createViewlink.actionType:
      {
        const {
          name,
          visibleOnAnalytics,
          viewerIdentification
        } = action.payload;
        return loop(produce(state, newState => {
          newState.viewlinks = setLoading(state.viewlinks);
        }), Cmd.run(() => createViewlink(state.documentOid, name, visibleOnAnalytics, viewerIdentification), {
          successActionCreator: viewLinkActions.createViewlinkSuccess,
          failActionCreator: viewLinkActions.createViewlinkFailure
        }));
      }
    case viewLinkActions.createViewlinkSuccess.actionType:
      {
        return produce(state, newState => {
          newState.viewlinks = setValue(state.viewlinks, state.viewlinks.value.append(action.payload.link_id, action.payload));
        });
      }
    case viewLinkActions.createViewlinkFailure.actionType:
      {
        return produce(state, newState => {
          newState.viewlinks = setError(state.viewlinks, action.payload);
        });
      }
    case viewLinkActions.removeViewlink.actionType:
      {
        const {
          linkId,
          deleteData
        } = action.payload;
        return loop(produce(state, newState => {
          newState.viewlinks = setLoading(state.viewlinks);
        }), Cmd.run(() => revokeViewlink(linkId, deleteData), {
          successActionCreator: viewLinkActions.removeViewlinkSuccess,
          failActionCreator: viewLinkActions.removeViewlinkFailure
        }));
      }
    case viewLinkActions.removeViewlinkSuccess.actionType:
      {
        return produce(state, newState => {
          newState.viewlinks = setValue(state.viewlinks, state.viewlinks.value.exclude(action.payload.link_id));
        });
      }
    case viewLinkActions.removeViewlinkFailure.actionType:
      {
        return produce(state, newState => {
          newState.viewlinks = setError(state.viewlinks, action.payload);
        });
      }
    case actions.getContacts.actionType:
      {
        return loop({
          ...state,
          contacts: setLoading(state.contacts)
        }, Cmd.run(() => getTeamMembers(), {
          successActionCreator: actions.getContactsSuccess,
          failActionCreator: actions.getContactsFailure
        }));
      }
    case actions.getContactsSuccess.actionType:
      {
        return produce(state, newState => {
          newState.contacts = setValue(state.contacts, EntityContainer.fromArray(action.payload, pathOr('', ['email'])));
        });
      }
    case actions.getContactsFailure.actionType:
      {
        return {
          ...state,
          contacts: setError(state.contacts, action.payload)
        };
      }
    case actions.getUnlistedLink.actionType:
      {
        return loop({
          ...state,
          unlistedLink: setLoading(state.unlistedLink)
        }, Cmd.run(() => getUnlistedShareLink(state.documentOid), {
          successActionCreator: actions.getUnlistedLinkSuccess,
          failActionCreator: actions.getUnlistedLinkFailure
        }));
      }
    case actions.getUnlistedLinkSuccess.actionType:
      {
        return {
          ...state,
          unlistedLink: setValue(state.unlistedLink, action.payload)
        };
      }
    case actions.getUnlistedLinkFailure.actionType:
      {
        return {
          ...state,
          unlistedLink: setError(state.unlistedLink, action.payload)
        };
      }
    case actions.generateUnlistedLink.actionType:
      {
        return loop({
          ...state,
          unlistedLink: setLoading(state.unlistedLink)
        }, Cmd.run(() => createOrRegenerateUnlistedShareLink(state.documentOid), {
          successActionCreator: actions.generateUnlistedLinkSuccess,
          failActionCreator: actions.generateUnlistedLinkFailure
        }));
      }
    case actions.generateUnlistedLinkSuccess.actionType:
      {
        return {
          ...state,
          unlistedLink: setValue(state.unlistedLink, action.payload)
        };
      }
    case actions.generateUnlistedLinkFailure.actionType:
      {
        return {
          ...state,
          unlistedLink: setError(state.unlistedLink, action.payload)
        };
      }
    case actions.deleteUnlistedLink.actionType:
      {
        return loop({
          ...state,
          unlistedLink: setLoading(state.unlistedLink)
        }, Cmd.run(() => deleteUnlistedLink(state.documentOid), {
          successActionCreator: actions.deleteUnlistedLinkSuccess,
          failActionCreator: actions.deleteUnlistedLinkFailure
        }));
      }
    case actions.deleteUnlistedLinkSuccess.actionType:
      {
        return {
          ...state,
          unlistedLink: setValue(state.unlistedLink, null)
        };
      }
    case actions.deleteUnlistedLinkFailure.actionType:
      {
        return {
          ...state,
          unlistedLink: setError(state.unlistedLink, action.payload)
        };
      }
    case actions.reloadPage.actionType:
      {
        return loop(state, Cmd.run(() => window.location.reload()));
      }
    case actions.getFolderDetails.actionType:
      {
        const {
          folderUuid
        } = action.payload;
        return loop({
          ...state,
          folderCollaborators: setLoading(state.folderCollaborators),
          foldersRestrictions: setLoading(state.foldersRestrictions)
        }, Cmd.run(() => FolderService.getFolderDetails(folderUuid, true), {
          successActionCreator: response => actions.getFolderDetailsSuccess(folderUuid, response),
          failActionCreator: actions.folderCollaboratorsError
        }));
      }
    case actions.getFolderDetailsSuccess.actionType:
      {
        const {
          folderUuid,
          details
        } = action.payload;
        return loop({
          ...state,
          foldersRestrictions: setValue(state.foldersRestrictions, state.foldersRestrictions.value.append(folderUuid, details.folder_restrictions))
        }, Cmd.action(actions.getFolderCollaboratorsSuccess(details.users)));
      }
    case actions.getFolderCollaboratorsSuccess.actionType:
      {
        const folderCollaborators = Object.values(action.payload).filter(collaboratorItem => collaboratorItem.details !== undefined).map(collaboratorItem => {
          return {
            username: collaboratorItem.details.username,
            email: collaboratorItem.details.email,
            name: collaboratorItem.details.name,
            id: collaboratorItem.details.id,
            avatar: collaboratorItem.details.avatar,
            role: collaboratorItem.role
          };
        });
        return {
          ...state,
          folderCollaborators: setValue(state.folderCollaborators, EntityContainer.fromArray(folderCollaborators, pathOr('', ['id'])))
        };
      }
    case actions.addFolderCollaborator.actionType:
      {
        const {
          email,
          folderUuid,
          successCallback
        } = action.payload;
        return loop(produce(state, newState => {
          newState.folderCollaborators = setLoading(state.folderCollaborators);
        }), Cmd.run(() => FolderService.addFolderCollaborator(folderUuid, email), {
          successActionCreator: () => {
            successCallback();
            return actions.updateFolderCollaboratorSuccess(folderUuid);
          },
          failActionCreator: error => actions.addFolderCollaboratorError(folderUuid, error)
        }));
      }
    case actions.removeFolderCollaborator.actionType:
      {
        const {
          collaboratorId,
          folderUuid,
          isRemovingSelf
        } = action.payload;
        return loop(state, Cmd.run(() => FolderService.removeFolderCollaborator(folderUuid, collaboratorId), {
          successActionCreator: () => isRemovingSelf ? actions.reloadPage() : actions.updateFolderCollaboratorSuccess(folderUuid),
          failActionCreator: actions.folderCollaboratorsError
        }));
      }
    case actions.updateFolderCollaboratorSuccess.actionType:
      {
        return loop({
          ...state,
          folderCollaborators: setLoading(state.folderCollaborators)
        }, Cmd.run(() => FolderService.getCollaboratorList(action.payload.folderUuid), {
          successActionCreator: actions.getFolderCollaboratorsSuccess,
          failActionCreator: actions.folderCollaboratorsError
        }));
      }
    case actions.folderCollaboratorsError.actionType:
      {
        return {
          ...state,
          folderCollaborators: setError(state.folderCollaborators, action.payload)
        };
      }
    case actions.addFolderCollaboratorError.actionType:
      {
        return produce(state, newState => {
          newState.folderCollaborators = setError(state.folderCollaborators, action.payload.error);
        });
      }
    case unifiedPrivacyActions.setPublished.actionType:
      return loop(produce(state, newState => {
        newState.unifiedPrivacy = setLoading(state.unifiedPrivacy);
      }), Cmd.run(() => setDocumentPublished(state.documentOid, action.payload.isPublished), {
        successActionCreator: unifiedPrivacyActions.setUnifiedPrivacySuccess,
        failActionCreator: unifiedPrivacyActions.setUnifiedPrivacyFailure
      }));
    case unifiedPrivacyActions.setReusable.actionType:
      return loop(produce(state, newState => {
        newState.unifiedPrivacy = setLoading(state.unifiedPrivacy);
      }), Cmd.run(() => setDocumentReusable(state.documentOid, action.payload.isReusable), {
        successActionCreator: unifiedPrivacyActions.setUnifiedPrivacySuccess,
        failActionCreator: unifiedPrivacyActions.setUnifiedPrivacyFailure
      }));
    case unifiedPrivacyActions.setUnifiedPrivacy.actionType:
      return loop(produce(state, newState => {
        newState.unifiedPrivacy = setLoading(state.unifiedPrivacy);
      }), Cmd.run(() => setDocumentUnifiedPrivacy(state.documentOid, action.payload.unifiedPrivacy), {
        successActionCreator: unifiedPrivacyActions.setUnifiedPrivacySuccess,
        failActionCreator: unifiedPrivacyActions.setUnifiedPrivacyFailure
      }));
    case unifiedPrivacyActions.setUnifiedPrivacySuccess.actionType:
      return produce(state, newState => {
        newState.unifiedPrivacy = setValue(state.unifiedPrivacy, action.payload);
      });
    case unifiedPrivacyActions.setUnifiedPrivacyFailure.actionType:
      return produce(state, newState => {
        newState.unifiedPrivacy = setError(state.unifiedPrivacy, action.payload);
      });
    case referralActions.getReferralData.actionType:
      {
        return loop({
          ...state,
          referral: setLoading(state.referral)
        }, Cmd.run(() => {
          var _state$currentUser, _state$currentUser2;
          if (!((_state$currentUser = state.currentUser) === null || _state$currentUser === void 0 ? void 0 : _state$currentUser.userOid)) {
            throw new Error('userOid not found');
          }
          return getReferralData((_state$currentUser2 = state.currentUser) === null || _state$currentUser2 === void 0 ? void 0 : _state$currentUser2.userOid, action.payload.campaignNanoid);
        }, {
          successActionCreator: referralActions.getReferralDataSuccess,
          failActionCreator: referralActions.getReferralDataFailure
        }));
      }
    case referralActions.getReferralDataSuccess.actionType:
      {
        return produce(state, newState => {
          newState.referral = setValue(state.referral, action.payload);
        });
      }
    case referralActions.getReferralDataFailure.actionType:
      {
        return produce(state, newState => {
          newState.referral = setError(state.referral, action.payload);
        });
      }
    default:
      {
        return state;
      }
  }
};
export default getReducer;